import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppInput, AppTextarea } from '../../ui';

function MagazineInlineText({
  value,
  displayValue,
  label,
  className = '',
  emptyClassName = '',
  multiline = false,
  rows = 3,
  type = 'text',
  disabled = false,
  placeholder = '—',
  onCommit,
}) {
  const text = value == null ? '' : String(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const skipCommit = useRef(false);
  const areaRef = useRef(null);

  const fitAreaHeight = () => {
    const node = areaRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    if (!editing || !multiline) return;
    fitAreaHeight();
  }, [editing, draft, multiline]);

  useEffect(() => {
    if (!editing) {
      setDraft(text);
    }
  }, [text, editing]);

  const closeWithoutSave = () => {
    skipCommit.current = true;
    setDraft(text);
    setEditing(false);
  };

  const commit = () => {
    if (skipCommit.current) {
      skipCommit.current = false;
      return;
    }
    setEditing(false);
    const next = draft ?? '';
    if (next === text) return;
    onCommit(next);
  };

  const startEdit = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    skipCommit.current = false;
    setDraft(text);
    setEditing(true);
  };

  const isEmpty = text.trim() === '';
  const shown = displayValue || (isEmpty ? placeholder : text);

  if (!editing) {
    const viewClass = [
      className,
      isEmpty && emptyClassName,
      !disabled && 'klub-magazine-card__editable',
    ].filter(Boolean).join(' ');

    const view = (
      <p
        className={viewClass}
        title={disabled ? undefined : 'Double-clic pour modifier'}
        onDoubleClick={startEdit}
      >
        {shown}
      </p>
    );

    if (!label) return view;

    return (
      <div className="klub-magazine-card__field">
        <span className="klub-magazine-card__field-label">{label}</span>
        {view}
      </div>
    );
  }

  const fieldProps = {
    size: 'sm',
    type: multiline ? undefined : type,
    step: type === 'number' ? '0.01' : undefined,
    className: `klub-magazine-card__inline ${className}`,
    value: draft,
    autoFocus: true,
    disabled,
    'aria-label': label || 'Modifier',
    inputRef: multiline ? areaRef : undefined,
    rows: multiline ? 1 : undefined,
    onChange: (event) => {
      setDraft(event.target.value);
      if (multiline) {
        event.target.style.height = 'auto';
        event.target.style.height = `${event.target.scrollHeight}px`;
      }
    },
    onFocus: (event) => {
      if (!multiline) event.target.select();
    },
    onBlur: commit,
    onKeyDown: (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeWithoutSave();
        return;
      }
      if (!multiline && event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
      }
      if (multiline && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
  };

  const field = multiline
    ? <AppTextarea rows={rows} {...fieldProps} />
    : <AppInput {...fieldProps} />;

  if (!label) return field;

  return (
    <div className="klub-magazine-card__field">
      <span className="klub-magazine-card__field-label">{label}</span>
      {field}
    </div>
  );
}

export default MagazineInlineText;
